import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Bare-root route (`''`). The real fac3less.com front page is the STATIC
 * landing (`assets/landing/index.html`), which nginx serves for `location = /`
 * on a full page load. This route is only reached by in-app navigation to `/`
 * (e.g. a logo link), so it hands over to the static page instead of keeping
 * a second, drifting copy of it:
 *  - built app (staging/prod): full page load of `/` → nginx serves the landing
 *  - dev (ng serve, no nginx): the same file under `/assets/landing/index.html`
 * A short sessionStorage guard stops a reload loop should `/` ever reach the
 * Angular shell in a built app (misrouted nginx); the fallback link is shown then.
 */
@Component({
  selector: 'app-landing-page',
  standalone: true,
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingPageComponent implements OnInit {
  private static readonly GUARD_KEY = 'fac3less.landingRedirectAt';
  private static readonly GUARD_MS = 10_000;

  readonly target = environment.production ? '/' : '/assets/landing/index.html';
  readonly showFallback = signal(false);

  ngOnInit(): void {
    const suffix = window.location.search + window.location.hash;
    if (this.recentlyRedirected()) {
      this.showFallback.set(true);
      return;
    }
    try {
      sessionStorage.setItem(LandingPageComponent.GUARD_KEY, String(Date.now()));
    } catch { /* private mode: redirect anyway */ }
    window.location.replace(this.target + suffix);
  }

  private recentlyRedirected(): boolean {
    try {
      const at = Number(sessionStorage.getItem(LandingPageComponent.GUARD_KEY));
      return !!at && Date.now() - at < LandingPageComponent.GUARD_MS;
    } catch {
      return false;
    }
  }
}
